/**
 * Orchestrateur de layout de l'article de blog.
 *
 * Problème : le banner, le sommaire et les produits sont des sections Shopify
 * distinctes, rendues comme des blocs pleine largeur empilés. On ne peut donc
 * pas les placer en rails gauche/droite autour du contenu avec du CSS seul.
 *
 * Solution : en desktop (>= 1000px), on crée une grille `.blog-post-grid` avec
 * trois colonnes [sommaire, centre, produits]. La colonne centrale est un
 * conteneur `.blog-post-grid__main` qui empile [banner, contenu] : ainsi les
 * rails flanquent aussi le banner et démarrent à la hauteur du titre.
 * En dessous, on démonte la grille et on laisse les sections empilées dans
 * l'ordre naturel du template (banner > contenu > sommaire > produits) — donc
 * aucun JS requis en mobile.
 *
 * Chargé par les deux sections rails (garde d'idempotence via window flag).
 */
(function () {
  if (window.__blogPostLayoutInit) return;
  window.__blogPostLayoutInit = true;

  const DESKTOP = window.matchMedia('(min-width: 1000px)');

  function contentSection() {
    const content = document.querySelector('.article-main');
    return content ? content.closest('.shopify-section') : null;
  }

  function bannerSection() {
    return document.querySelector('.shopify-section--blog-post-banner');
  }

  // Une section rail ne "compte" que si elle est visible et contient réellement
  // du contenu balisé (le sommaire se masque lui-même s'il a < 2 titres ;
  // la section produits ne rend rien si le metafield est vide).
  function railSection(name) {
    const sec = document.querySelector('.shopify-section--blog-post-' + name);
    if (!sec || sec.hidden) return null;
    if (!sec.querySelector('[data-blog-post-rail="' + name + '"]')) return null;
    return sec;
  }

  function teardownGrid() {
    const grid = document.querySelector('.blog-post-grid');
    if (!grid) return;

    // On démonte la grille et son conteneur pleine largeur (le wrapper est
    // absent des vieilles instances, d'où le fallback sur la grille).
    const wrapper = grid.closest('.blog-post-layout') || grid;

    const banner = grid.querySelector('.shopify-section--blog-post-banner');
    const content = grid.querySelector('.shopify-section--main-article');
    // Les rails sont les sections restantes, hors conteneur central.
    const rails = Array.from(grid.children).filter(
      (c) => c.classList.contains('shopify-section')
    );

    const frag = document.createDocumentFragment();
    if (banner) frag.appendChild(banner); // banner…
    if (content) frag.appendChild(content); // …puis contenu…
    rails.forEach((c) => frag.appendChild(c)); // …puis sommaire, produits.
    wrapper.replaceWith(frag);
  }

  function layout() {
    const content = contentSection();
    if (!content) {
      teardownGrid();
      return;
    }

    const banner = bannerSection();
    const toc = railSection('toc');
    const products = railSection('products');

    if (!DESKTOP.matches || (!toc && !products)) {
      teardownGrid();
      return;
    }

    let grid = document.querySelector('.blog-post-grid');
    if (!grid) {
      // Conteneur pleine largeur qui porte `overflow-x: clip` : il contient le
      // full-bleed du banner (bande de fond en ~100vw) sans le rogner, tout en
      // évitant le scroll horizontal. Le clip ne peut pas vivre sur la grille
      // elle-même, qui est centrée et limitée à `--blog-grid-max` : il couperait
      // la bande au bord de la grille au lieu du bord du viewport.
      const wrapper = document.createElement('div');
      wrapper.className = 'blog-post-layout';
      grid = document.createElement('div');
      grid.className = 'blog-post-grid';
      wrapper.appendChild(grid);
      (banner || content).before(wrapper);
    }

    grid.classList.toggle('blog-post-grid--toc', !!toc);
    grid.classList.toggle('blog-post-grid--products', !!products);

    // Colonne centrale : banner + contenu empilés.
    let main = grid.querySelector('.blog-post-grid__main');
    if (!main) {
      main = document.createElement('div');
      main.className = 'blog-post-grid__main';
    }

    // Ordre des colonnes : sommaire, [banner + contenu], produits.
    if (toc) grid.appendChild(toc);
    grid.appendChild(main);
    if (banner) main.appendChild(banner);
    main.appendChild(content);
    if (products) grid.appendChild(products);
  }

  function schedule() {
    layout();
    // Second passage après upgrade des web components (ex. le sommaire se masque).
    window.requestAnimationFrame(layout);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  window.addEventListener('load', layout);
  DESKTOP.addEventListener('change', layout);

  // Un rail change d'état (sommaire masqué/affiché) → recalcul.
  document.addEventListener('blog-post-rail:change', layout);

  // Theme editor : re-render / réordonnancement des sections.
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', layout);
  document.addEventListener('shopify:section:reorder', layout);
})();
